import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigationType } from 'react-router-dom';

// Offsets live in a module-level map rather than sessionStorage on purpose:
// they are only meaningful while react-query still holds the list pages that
// made the document that tall, and that cache dies with the tab too.
const scrollPositions = new Map<string, number>();

// The row the user opened on their way out, so the list can point back at it.
// Same lifetime rules as the offset above, for the same reason.
const visitedRows = new Map<string, string>();

// The cached pages render in one go, but web fonts and images can still shift
// the document height for a few frames, so the restore keeps retrying until
// the page is tall enough to hold the offset.
const MAX_RESTORE_FRAMES = 60;

/**
 * Returns the window to where the user left a list page, so opening a row and
 * coming back does not drop them at the top of the list again.
 *
 * `isReady` should flip once the query has settled - restoring before the rows
 * are in the DOM would just clamp the offset to a document that is still empty.
 * `filters` are the values that rebuild the list from page one; changing any of
 * them drops the stored offset, since it no longer points at anything.
 *
 * Also hands back the row the user opened last time, so the list can highlight
 * where they came back from.
 */
export const useScrollRestoration = (
  key: string,
  isReady: boolean,
  filters: unknown[] = []
) => {
  const navigationType = useNavigationType();
  // Coming back from a detail page is either a browser POP or the in-page Back
  // button, which navigates with `replace`. A PUSH is a fresh visit from the
  // menu and should start at the top.
  const shouldRestoreRef = useRef(navigationType !== 'PUSH');
  const hasSettledRef = useRef(false);
  // Read in the initialiser rather than an effect: the PUSH cleanup below runs
  // after the first render, which would be too late to keep a stale id off it.
  const [visitedRowId, setVisitedRowId] = useState<string | undefined>(() =>
    navigationType === 'PUSH' ? undefined : visitedRows.get(key)
  );

  useEffect(() => {
    if (hasSettledRef.current) {
      return;
    }

    if (!shouldRestoreRef.current) {
      scrollPositions.delete(key);
      visitedRows.delete(key);
      hasSettledRef.current = true;
      return;
    }

    const target = scrollPositions.get(key);
    if (target === undefined) {
      hasSettledRef.current = true;
      return;
    }
    if (!isReady) {
      return;
    }

    let frames = 0;
    let frameId: number;
    const restore = () => {
      if (hasSettledRef.current) {
        return;
      }
      const maxScroll = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        0
      );
      // Falling back to `maxScroll` matters when the list cache expired while
      // the user was away and only the first page came back: landing at the
      // bottom pulls in the next page, walking the list back towards where they
      // left it instead of stranding them at the top.
      if (maxScroll >= target || frames >= MAX_RESTORE_FRAMES) {
        window.scrollTo(0, Math.min(target, maxScroll));
        hasSettledRef.current = true;
        return;
      }
      frames++;
      frameId = requestAnimationFrame(restore);
    };
    frameId = requestAnimationFrame(restore);

    return () => cancelAnimationFrame(frameId);
  }, [key, isReady]);

  // Recording only starts once the restore has settled, otherwise the scroll
  // events fired while the page is still at the top would overwrite the very
  // offset we are about to restore.
  useEffect(() => {
    const handleScroll = () => {
      if (hasSettledRef.current) {
        scrollPositions.set(key, window.scrollY);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [key]);

  const filtersKey = JSON.stringify(filters);
  const previousFiltersKeyRef = useRef(filtersKey);
  useEffect(() => {
    // Initialised to the current value, so this is a no-op on the first render
    // and only fires once the user actually searches or sorts.
    if (previousFiltersKeyRef.current === filtersKey) {
      return;
    }
    previousFiltersKeyRef.current = filtersKey;
    scrollPositions.delete(key);
    visitedRows.delete(key);
    setVisitedRowId(undefined);
    hasSettledRef.current = true;
    window.scrollTo(0, 0);
  }, [key, filtersKey]);

  // Only stored, not applied to the current render: by the time this is called
  // the user is on their way to the row's detail page.
  const rememberVisitedRow = useCallback(
    (rowId: string) => {
      visitedRows.set(key, rowId);
    },
    [key]
  );

  return { visitedRowId, rememberVisitedRow };
};
